// src/pages/creator-panel/CompanySettingsPage.js
// Company Settings page for Creator Panel

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    CircularProgress,
    Alert,
    Snackbar,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import SaveIcon from '@mui/icons-material/Save';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BadgeIcon from '@mui/icons-material/Badge';
import axiosInstance from '../../utils/axiosInstance';
import { useCompanySettings } from '../../contexts/CompanySettingsContext';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const colors = {
    textPrimary: '#1e293b',
    textSecondary: '#475569',
    textTertiary: '#94a3b8',
    accent: '#3b82f6',
    success: '#059669',
    danger: '#dc2626',
};

export default function CompanySettingsPage() {
    const { updateCompanyLogo } = useCompanySettings();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const [formData, setFormData] = useState({
        company_name: '',
        company_email: '',
        company_phone: '',
        company_address: '',
    });
    const [logoPreview, setLogoPreview] = useState(null);
    const [logoFile, setLogoFile] = useState(null);
    const fileInputRef = useRef(null);

    const fetchSettings = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get('/settings/company-settings');
            if (response.data?.success) {
                const data = response.data.data;
                setFormData({
                    company_name: data.company_name || '',
                    company_email: data.company_email || '',
                    company_phone: data.company_phone || '',
                    company_address: data.company_address || '',
                });
                if (data.company_logo_url) {
                    setLogoPreview(data.company_logo_url);
                }
            }
        } catch (err) {
            console.error('Failed to fetch company settings:', err);
            setSnackbar({ open: true, message: 'Failed to load company settings.', severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        document.title = 'Company Settings | Creator Panel';
        fetchSettings();
    }, [fetchSettings]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            setLogoFile(file);
            const reader = new FileReader();
            reader.onloadend = () => setLogoPreview(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const file = e.dataTransfer.files?.[0];
        if (file && file.type.startsWith('image/')) {
            setLogoFile(file);
            const reader = new FileReader();
            reader.onloadend = () => setLogoPreview(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const submissionData = new FormData();
            submissionData.append('company_name', formData.company_name);
            submissionData.append('company_email', formData.company_email);
            submissionData.append('company_phone', formData.company_phone);
            submissionData.append('company_address', formData.company_address);
            if (logoFile) {
                submissionData.append('company_logo', logoFile);
            }

            const response = await axiosInstance.post('/settings/company-settings', submissionData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            if (response.data?.success) {
                setSnackbar({ open: true, message: 'Company settings saved successfully!', severity: 'success' });
                setLogoFile(null);
                if (response.data.data?.company_logo_url) {
                    setLogoPreview(response.data.data.company_logo_url);
                    updateCompanyLogo(response.data.data.company_logo_url);
                }
            } else {
                setSnackbar({ open: true, message: response.data?.message || 'Failed to save settings.', severity: 'error' });
            }
        } catch (err) {
            console.error('Failed to save company settings:', err);
            setSnackbar({
                open: true,
                message: err.response?.data?.message || 'Failed to save settings.',
                severity: 'error',
            });
        } finally {
            setSaving(false);
        }
    };

    const handleSnackbarClose = () => setSnackbar({ ...snackbar, open: false });

    if (loading) {
        return (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '60vh',
                gap: 2,
            }}>
                <CircularProgress size={40} sx={{ color: colors.accent }} />
                <Typography sx={{ fontFamily, color: colors.textSecondary, fontSize: '0.9rem' }}>
                    Loading company settings...
                </Typography>
            </Box>
        );
    }

    const inputSx = {
        '& .MuiInputBase-root': { fontFamily, borderRadius: '12px', bgcolor: '#fff' },
        '& .MuiInputLabel-root': { fontFamily },
        '& .MuiOutlinedInput-root': {
            '& fieldset': { borderColor: 'rgba(0,0,0,0.08)' },
            '&:hover fieldset': { borderColor: colors.accent },
        },
    };

    return (
        <Box sx={{ fontFamily, maxWidth: '1200px', width: '100%', mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Box sx={{ textAlign: 'center', mb: 1.5 }}>
                    <Box sx={{
                        width: 56,
                        height: 56,
                        borderRadius: '16px',
                        background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 2,
                        boxShadow: '0 8px 24px rgba(59,130,246,0.3)',
                    }}>
                        <BusinessIcon sx={{ color: '#fff', fontSize: 28 }} />
                    </Box>
                    <Typography sx={{
                        fontFamily,
                        fontSize: '1.5rem',
                        fontWeight: 700,
                        color: colors.textPrimary,
                        letterSpacing: '-0.02em',
                    }}>
                        Company Settings
                    </Typography>
                    <Typography sx={{ fontFamily, fontSize: '0.8rem', color: colors.textTertiary, mt: 0.5 }}>
                        Manage your organization's details and branding
                    </Typography>
                </Box>
            </Box>

            {/* Main Content */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
                {/* Left: Form Fields */}
                <Box sx={{ flex: 2 }}>
                    <Box sx={{
                        p: 3,
                        borderRadius: '14px',
                        bgcolor: 'rgba(255,255,255,0.8)',
                        backdropFilter: 'blur(12px)',
                        border: '1px solid rgba(0,0,0,0.06)',
                    }}>
                        <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.95rem', color: colors.textPrimary, mb: 2.5 }}>
                            Company Information
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                            {/* Company Name */}
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                                    <BadgeIcon sx={{ fontSize: '0.95rem', color: colors.textTertiary }} />
                                    <Typography sx={{ fontFamily, fontSize: '0.8rem', fontWeight: 600, color: colors.textSecondary }}>
                                        Company Name
                                    </Typography>
                                </Box>
                                <TextField
                                    name="company_name"
                                    fullWidth
                                    size="small"
                                    value={formData.company_name}
                                    onChange={handleInputChange}
                                    placeholder="Your Company LLC"
                                    sx={inputSx}
                                />
                            </Box>

                            {/* Contact Email */}
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                                    <EmailIcon sx={{ fontSize: '0.95rem', color: colors.textTertiary }} />
                                    <Typography sx={{ fontFamily, fontSize: '0.8rem', fontWeight: 600, color: colors.textSecondary }}>
                                        Contact Email
                                    </Typography>
                                </Box>
                                <TextField
                                    name="company_email"
                                    type="email"
                                    fullWidth
                                    size="small"
                                    value={formData.company_email}
                                    onChange={handleInputChange}
                                    placeholder="contact@yourcompany.com"
                                    sx={inputSx}
                                />
                            </Box>

                            {/* Contact Phone */}
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                                    <PhoneIcon sx={{ fontSize: '0.95rem', color: colors.textTertiary }} />
                                    <Typography sx={{ fontFamily, fontSize: '0.8rem', fontWeight: 600, color: colors.textSecondary }}>
                                        Contact Phone
                                    </Typography>
                                </Box>
                                <TextField
                                    name="company_phone"
                                    type="tel"
                                    fullWidth
                                    size="small"
                                    value={formData.company_phone}
                                    onChange={handleInputChange}
                                    placeholder="+1 (555) 123-4567"
                                    sx={inputSx}
                                />
                            </Box>

                            {/* Company Address */}
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                                    <LocationOnIcon sx={{ fontSize: '0.95rem', color: colors.textTertiary }} />
                                    <Typography sx={{ fontFamily, fontSize: '0.8rem', fontWeight: 600, color: colors.textSecondary }}>
                                        Company Address
                                    </Typography>
                                </Box>
                                <TextField
                                    name="company_address"
                                    fullWidth
                                    size="small"
                                    multiline
                                    rows={3}
                                    value={formData.company_address}
                                    onChange={handleInputChange}
                                    placeholder="123 Business Rd, Suite 100, City, State 12345"
                                    sx={inputSx}
                                />
                            </Box>
                        </Box>
                    </Box>
                </Box>

                {/* Right: Logo Upload */}
                <Box sx={{ flex: 1 }}>
                    <Box sx={{
                        p: 3,
                        borderRadius: '14px',
                        bgcolor: 'rgba(255,255,255,0.8)',
                        backdropFilter: 'blur(12px)',
                        border: '1px solid rgba(0,0,0,0.06)',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                    }}>
                        <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.95rem', color: colors.textPrimary, mb: 2.5 }}>
                            Company Logo
                        </Typography>

                        <Box
                            onClick={() => fileInputRef.current?.click()}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                            sx={{
                                flex: 1,
                                minHeight: 220,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '2px dashed',
                                borderColor: 'rgba(0,0,0,0.1)',
                                borderRadius: '12px',
                                cursor: 'pointer',
                                bgcolor: 'rgba(248,250,252,0.8)',
                                transition: 'all 0.2s ease',
                                overflow: 'hidden',
                                '&:hover': {
                                    borderColor: colors.accent,
                                    bgcolor: 'rgba(59,130,246,0.04)',
                                    transform: 'translateY(-1px)',
                                },
                            }}
                        >
                            {logoPreview ? (
                                <Box
                                    component="img"
                                    src={logoPreview}
                                    alt="Company Logo"
                                    sx={{
                                        maxWidth: '100%',
                                        maxHeight: '100%',
                                        objectFit: 'contain',
                                        p: 2,
                                    }}
                                />
                            ) : (
                                <Box sx={{ textAlign: 'center', p: 2 }}>
                                    <Box sx={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: '12px',
                                        bgcolor: 'rgba(59,130,246,0.1)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mx: 'auto',
                                        mb: 1.5,
                                    }}>
                                        <CloudUploadIcon sx={{ fontSize: 24, color: colors.accent }} />
                                    </Box>
                                    <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: colors.textSecondary }}>
                                        Click to upload
                                    </Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: colors.textTertiary, mt: 0.5 }}>
                                        or drag and drop
                                    </Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.7rem', color: colors.textTertiary, mt: 1 }}>
                                        PNG, JPG, or GIF (200x200px)
                                    </Typography>
                                </Box>
                            )}
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/png, image/jpeg, image/gif"
                                onChange={handleFileChange}
                                style={{ display: 'none' }}
                            />
                        </Box>

                        {logoPreview && (
                            <Typography
                                onClick={(e) => { e.stopPropagation(); setLogoPreview(null); setLogoFile(null); }}
                                sx={{
                                    fontFamily,
                                    fontSize: '0.75rem',
                                    color: colors.danger,
                                    cursor: 'pointer',
                                    mt: 1.5,
                                    textAlign: 'center',
                                    fontWeight: 500,
                                    '&:hover': { textDecoration: 'underline' },
                                }}
                            >
                                Remove logo
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Save Button */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                mt: 3,
            }}>
                <Button
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving}
                    sx={{
                        fontFamily,
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        borderRadius: '12px',
                        px: 3,
                        py: 1.25,
                        background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                        boxShadow: '0 4px 16px rgba(59,130,246,0.3)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #1d4ed8, #3b82f6)',
                            transform: 'translateY(-1px)',
                        },
                        transition: 'all 0.2s ease',
                    }}
                >
                    {saving ? 'Saving...' : 'Save Changes'}
                </Button>
            </Box>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%', fontFamily, borderRadius: '10px' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
